/**
 * Asset Handover Acknowledgment Modal
 * Allows users to acknowledge receiving an asset with optional signature
 */
import React, { useState, useRef, useEffect } from 'react';
import { X, CheckCircle, FileSignature, AlertCircle, Package } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateOnly } from '../../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem('token');

const apiCall = async (endpoint, options = {}) => {
  const token = getToken();
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  
  return response.json();
};

// Simple Signature Pad Component
const SignaturePad = ({ onSignatureChange, onClear }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#60a5fa';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);
  
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };
  
  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const coords = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };
  
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const coords = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setHasSignature(true);
  };
  
  const stopDrawing = () => {
    if (isDrawing && hasSignature) {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL('image/png').split(',')[1];
      onSignatureChange(signatureData);
    }
    setIsDrawing(false);
  };
  
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onClear();
  };
  
  return (
    <div className="signature-pad-container">
      <label className="signature-label">
        <FileSignature size={14} />
        Sign below to acknowledge receipt
      </label>
      <div className="signature-canvas-wrapper">
        <canvas
          ref={canvasRef}
          width={400}
          height={150}
          className="signature-canvas"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <button 
          type="button" 
          className="btn-clear-signature"
          onClick={clearCanvas}
        >
          Clear
        </button>
      </div>
      <span className="signature-hint">Draw your signature using mouse or touch</span>
    </div>
  );
};

// Assignment Info Component
const AssignmentInfo = ({ assignment }) => {
  if (!assignment) return null;
  
  return (
    <div className="assignment-info-card">
      <div className="asset-header">
        <Package size={24} />
        <div>
          <h4>{assignment.asset_name}</h4>
          <code className="asset-tag">{assignment.asset_tag}</code>
        </div>
      </div>
      
      <div className="info-grid">
        <div className="info-item">
          <span className="label">Assigned By</span>
          <span className="value">{assignment.assigned_by_name}</span>
        </div>
        <div className="info-item">
          <span className="label">Assigned On</span>
          <span className="value">{formatDateOnly(assignment.assigned_at)}</span>
        </div>
        <div className="info-item">
          <span className="label">Condition</span>
          <span className={`condition-badge cond-${assignment.condition_at_handover}`}>
            {assignment.condition_at_handover}
          </span>
        </div>
        {assignment.expected_return_date && (
          <div className="info-item">
            <span className="label">Expected Return</span>
            <span className="value">{assignment.expected_return_date}</span>
          </div>
        )}
        {assignment.notes && (
          <div className="info-item full-width">
            <span className="label">Notes</span>
            <span className="value">{assignment.notes}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Acknowledgment Modal Component
const AcknowledgmentModal = ({ isOpen, onClose, assignment, onAcknowledged }) => {
  const [signature, setSignature] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      setSignature(null);
      setAcknowledged(false);
    }
  }, [isOpen]);
  
  const handleAcknowledge = async () => {
    setIsSubmitting(true);
    try {
      await apiCall(`/api/it-assets/assignments/${assignment.id}/acknowledge`, {
        method: 'POST',
        body: JSON.stringify({
          assignment_id: assignment.id,
          acknowledged: true,
          signature: signature
        })
      });
      
      setAcknowledged(true);
      toast.success('Asset receipt acknowledged successfully');
      
      if (onAcknowledged) {
        setTimeout(() => {
          onAcknowledged();
          onClose();
        }, 1500);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen || !assignment) return null;
  
  // Check if already acknowledged
  if (assignment.acknowledged) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content acknowledgment-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Asset Acknowledgment</h3>
            <button className="modal-close" onClick={onClose}><X size={18} /></button>
          </div>
          <div className="modal-body">
            <div className="already-acknowledged">
              <CheckCircle size={48} />
              <h4>Already Acknowledged</h4>
              <p>This asset was acknowledged on {formatDateOnly(assignment.acknowledged_at)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large acknowledgment-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-row">
            <FileSignature size={18} />
            <h3>Acknowledge Asset Receipt</h3>
          </div>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        
        <div className="modal-body">
          {acknowledged ? (
            <div className="acknowledgment-success">
              <CheckCircle size={64} />
              <h3>Thank You!</h3>
              <p>Your acknowledgment has been recorded.</p>
            </div>
          ) : (
            <>
              <div className="acknowledgment-notice">
                <AlertCircle size={18} />
                <p>
                  By signing below, you confirm that you have received this asset in the condition 
                  described and agree to be responsible for its proper care and use.
                </p>
              </div>
              
              <AssignmentInfo assignment={assignment} />
              
              <SignaturePad 
                onSignatureChange={setSignature}
                onClear={() => setSignature(null)}
              />
              
              <div className="modal-actions">
                <button type="button" onClick={onClose} className="btn-secondary">
                  Cancel
                </button>
                <button 
                  className="btn-primary btn-acknowledge"
                  onClick={handleAcknowledge}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'I Acknowledge Receipt'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcknowledgmentModal;
